import { clsx } from "clsx";
import { type CSSProperties, type FC, useCallback, useState } from "react";
import type { PressEvent } from "react-aria";

import { useIsMounted } from "../../hooks.ts";
import type { CommonDialogProps } from "../../types/dialogs.d.ts";
import { ActionButton } from "../ActionButton/ActionButton.tsx";
import { ActionButtonIntent } from "../ActionButton/settings.ts";
import { Callout } from "../Callout/Callout.tsx";
import { CalloutIntent } from "../Callout/settings.ts";
import style from "./TabDialog.module.css";

interface DialogButtonsWithErrorCalloutProps
  extends Pick<CommonDialogProps, "closeFn" | "mainButton" | "secondaryButton" | "errorCallout" | "cancelButtonLabel"> {
  className?: string;
  style?: CSSProperties;
}

/** Do not expose outside, internal use only. */
const DialogButtonsWithErrorCallout: FC<DialogButtonsWithErrorCalloutProps> = (props) => {
  const { mainButton } = props;
  const mainButtonOnPress = mainButton?.onPress;

  const isMounted = useIsMounted();
  const [mainError, setMainError] = useState<Error | string>();
  const handleMainPress = useCallback(
    (event: PressEvent) => {
      const response = mainButtonOnPress?.(event);

      // reset error
      setMainError(undefined);

      // parse error
      if (typeof (response as Promise<unknown> | undefined)?.then === "function") {
        return (response as Promise<unknown>).catch((error: string | Error | undefined) => {
          if (((error as Error | undefined)?.message || typeof error === "string") && isMounted) setMainError(error);
          return error;
        });
      }

      return response;
    },
    [mainButtonOnPress, isMounted],
  );

  return (
    <div className={clsx(style.footer, props.className)} style={props.style} data-testid="footer">
      {props.errorCallout ? (
        <Callout
          title={props.errorCallout.title}
          intent={props.errorCallout.intent ?? CalloutIntent.Danger}
          inline
          className={style.flexible_callout}
        >
          {props.errorCallout.message}
        </Callout>
      ) : (
        mainError && (
          <Callout intent={CalloutIntent.Danger} inline className={style.flexible_callout}>
            {(mainError as Error).message ?? mainError}
          </Callout>
        )
      )}
      <div className={style.buttons}>
        {props.secondaryButton != undefined && (
          <ActionButton intent={ActionButtonIntent.Outlined} {...props.secondaryButton} />
        )}
        <div className={style.button_group}>
          {typeof props.closeFn === "function" && (
            <ActionButton
              label={props.cancelButtonLabel || "Cancel"}
              onPress={props.closeFn}
              intent={ActionButtonIntent.Secondary}
            />
          )}
          {mainButton != undefined && (
            <ActionButton intent={ActionButtonIntent.Primary} {...{ ...mainButton, onPress: handleMainPress }} />
          )}
        </div>
      </div>
    </div>
  );
};

export { DialogButtonsWithErrorCallout };
